import { useEffect, useState } from "react";
import { checkEmpty, checkEmptyObj, checkIf, checkUndefined } from "../../utils";

export interface ILanguageEntity {
  en?: string;
  ar?: string;
}

export type AddressItem = {
  detail?: string;
  name?: string;
  zone?: number;
  area?: string;
  street?: number;
  building?: number;
  flat?: number;
};

interface AddressValidation {
  address?: AddressItem;
}

type AddressItemKey = "detail" | "zone" | "area" | "street" | "building" | "flat" | "name";

export type IValidateEntity = {
  error?: boolean;
  helperText?: string;
  dirty?: boolean;
};

interface IValidate {
  show?: boolean;
  error?: boolean;
  dirty?: boolean;
  message?: string;
  name?: IValidateEntity;
  zone?: IValidateEntity;
  area?: IValidateEntity;
  detail?: IValidateEntity;
  building?: IValidateEntity;
  flat?: IValidateEntity;
  street?: IValidateEntity;
}

export default function useAddressValidation(validationInfo: AddressItem) {
  const [validation, setValidation] = useState<IValidate>({
    show: false,
    error: false,
    dirty: false,
    message: "",
  });

  useEffect(() => {
    if (!checkIf(validationInfo) || checkEmptyObj(validationInfo)) {
      setValidation((state) => ({ ...state, error: true, zone: { error: true, helperText: "Invalid value" } }));
      return;
    }

    setValidation((state) => {
      let newState = { ...state } as IValidate;
      if (Object.keys(validation).length === 4) {
        let fields: any = {};
        Object.keys(validationInfo).forEach((item: string) => {
          if (typeof validationInfo[item as keyof AddressItem] === "string") {
            fields[item] = { error: false, helperText: "" };
          } else {
            fields[item] = { en: { error: false, helperText: "" }, ar: { error: false, helperText: "" } };
          }
        });
        newState = { ...newState, ...fields };
      }

      newState.error = false;

      if (validationInfo) {
        Object.entries(validationInfo).forEach(([key, value]) => {
          const numberRegex = /^[-+]?[0-9]+(\.[0-9]+)*$/;
          if (key === "detail") {
            newState.detail = {
              error: false,
              helperText: "",
            };
          } else if (key === "area") {
            newState.area = {
              error: false,
              helperText: "",
            };
          } else if (key === "name") {
            newState.name = {
              error: checkEmpty(value),
              helperText: "Please enter a name for the address",
            };
          } else if (key === "zone") {
            newState.zone = {
              error: !Boolean(value?.toString().match(numberRegex)),
              helperText: "Please select a zone",
            };
          } else {
            newState[key as AddressItemKey] = {
              error: !checkEmpty(value) && !Boolean(value?.toString().match(numberRegex)),
              helperText: "Numeric value needed",
            };
          }
        });
        newState.error = Boolean(
          newState.flat?.error ||
            newState.street?.error ||
            newState.building?.error ||
            newState.zone?.error ||
            newState.name?.error
        );
      } else {
        newState.error = true;
      }

      return newState;
    });
  }, [validationInfo]);
  return { validation, setValidation };
}
